import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeaturedDrinks } from '../api/queries';
import { MediaImage, LoadingBlock, EmptyState } from '../components/ui';
import { formatMoney } from '../lib/format';

interface SignatureData {
  heading?: string;
  linkLabel?: string;
  linkHref?: string;
}

/**
 * Signature drinks carousel. Items come from the menu (IsFeatured), so staff control what
 * appears here from the dashboard. Supports arrows, dots, keyboard arrows and swipe.
 */
export function SignatureCarouselBlock({ data }: { data: SignatureData }) {
  const { data: drinks, isLoading } = useFeaturedDrinks();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const items = drinks ?? [];
  const active = items[index % Math.max(items.length, 1)];

  const step = (direction: number) => {
    if (items.length === 0) return;
    setIndex((current) => (current + direction + items.length) % items.length);
  };

  return (
    <section className="sz-section" aria-labelledby="signature-heading">
      <div className="sz-section__inner">
        <div className="sz-section-head">
          <h2 id="signature-heading">{data.heading ?? 'Signature Drinks'}</h2>
          {data.linkHref && (
            <Link to={data.linkHref} className="sz-link-caps">
              {data.linkLabel ?? 'View Full Menu →'}
            </Link>
          )}
        </div>

        {isLoading && <LoadingBlock label="Loading signature drinks" />}

        {!isLoading && items.length === 0 && (
          <EmptyState message="No signature drinks are published yet." />
        )}

        {active && (
          <div
            className="sz-signature"
            onTouchStart={(event) => {
              touchStartX.current = event.touches[0]?.clientX ?? null;
            }}
            onTouchEnd={(event) => {
              const start = touchStartX.current;
              const end = event.changedTouches[0]?.clientX;
              if (start == null || end == null) return;
              const delta = end - start;
              if (Math.abs(delta) > 40) step(delta < 0 ? 1 : -1);
              touchStartX.current = null;
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') step(1);
              if (event.key === 'ArrowLeft') step(-1);
            }}
            role="group"
            aria-roledescription="carousel"
            aria-label="Signature drinks"
            tabIndex={0}
          >
            <MediaImage
              media={active.image}
              alt={active.name}
              className="sz-signature__media"
              placeholder={active.name}
              sizes="(max-width: 900px) 100vw, 50vw"
            />

            <div className="sz-signature__copy" aria-live="polite">
              <p className="sz-eyebrow sz-eyebrow--tight">Signature</p>
              <h3 className="sz-signature__name">{active.name}</h3>
              {active.description && <p className="sz-signature__desc">{active.description}</p>}
              {active.ingredients && <p className="sz-signature__ingredients">{active.ingredients}</p>}
              <p className="sz-signature__price">{formatMoney(active.price, active.currency)}</p>

              <Link to={`/menu/drinks/${active.slug}`} className="sz-link-caps sz-signature__details">
                View Details →
              </Link>

              <div className="sz-signature__controls">
                <button
                  type="button"
                  className="sz-carousel-arrow"
                  aria-label="Previous drink"
                  onClick={() => step(-1)}
                  disabled={items.length < 2}
                >
                  ←
                </button>
                <button
                  type="button"
                  className="sz-carousel-arrow"
                  aria-label="Next drink"
                  onClick={() => step(1)}
                  disabled={items.length < 2}
                >
                  →
                </button>
                <div className="sz-carousel-dots">
                  {items.map((item, dotIndex) => (
                    <button
                      key={item.id}
                      type="button"
                      className="sz-carousel-dot"
                      data-active={dotIndex === index}
                      aria-label={`Show ${item.name}`}
                      aria-current={dotIndex === index}
                      onClick={() => setIndex(dotIndex)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
